/**
 * CableJunctionDrawer
 *
 * Drawer component for viewing and editing cable junction (junction box) details.
 * Allows editing name, assigning products (enclosures/boxes), and viewing connected paths.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { useAppStore, useSelectedCableJunctions } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import {
  X,
  Trash2,
  Box,
  Cable,
  Plus,
  Package,
  ExternalLink,
  SquarePen,
  ArrowDownRight,
  Target,
  Tag,
  Maximize2,
} from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { JunctionPurposeType } from '@/types/cablePaths';
import { toast } from 'sonner';
import { useCableJunctionsStandardized } from '@/hooks/entities/useCableJunctionsStandardized';
import { useCablePathsStandardized } from '@/hooks/entities/useCablePathsStandardized';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { ProductSelectionModal } from '@/features/product-library/components';
import { useProduct } from '@/features/product-library/hooks/useProductLibrary';
import type { MarkerSizesConfig } from '@/types/floorPlanViewer';

export function CableJunctionDrawer() {
  // Store state
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const selectedCableJunctions = useSelectedCableJunctions();
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const selectEntity = useAppStore(state => state.selectEntity);
  const setActiveMode = useAppStore(state => state.setActiveMode);

  // FloorPlan context - use shared data to ensure all components see the same metadata
  const { floorPlan, data } = useFloorPlanContext();
  const queryClient = useQueryClient();

  // Get scale data
  const { scaleData } = useFloorPlanScale(floorPlan.id);

  // Get floor plan metadata from shared context (not a separate hook instance)
  // This ensures when we save, all components using the context see the update
  const { floorPlanMetadata, saveFloorPlanMetadata } = data;

  // Get junctions and paths
  const { junctions, junctionCoordMap, updateJunction, deleteJunction } =
    useCableJunctionsStandardized(floorPlan.id);
  const { paths: cablePaths } = useCablePathsStandardized(
    floorPlan.id,
    junctionCoordMap,
    scaleData,
  );

  // Local state
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [tempNotes, setTempNotes] = useState('');
  const [tempPurposeLabel, setTempPurposeLabel] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [tempScale, setTempScale] = useState(1.0);
  const [hasUnsavedScale, setHasUnsavedScale] = useState(false);
  const [tempLabelFontSize, setTempLabelFontSize] = useState(10);
  const [hasUnsavedLabelFontSize, setHasUnsavedLabelFontSize] = useState(false);

  // Computed values
  const isOpen = activeDrawer === 'cableJunction' && selectedCableJunctions.size > 0;
  const selectedJunctionId =
    selectedCableJunctions.size === 1 ? Array.from(selectedCableJunctions)[0] : null;
  const selectedJunction = selectedJunctionId
    ? junctions?.find(j => j.id === selectedJunctionId)
    : null;

  // Sync temp values when junction changes (must be after selectedJunction is defined)
  useEffect(() => {
    setTempNotes(selectedJunction?.notes || '');
    setTempPurposeLabel(selectedJunction?.purpose_label || '');
    setTempScale(selectedJunction?.scale || 1.0);
    setHasUnsavedScale(false);
    setTempLabelFontSize(selectedJunction?.label_font_size ?? 10);
    setHasUnsavedLabelFontSize(false);
  }, [
    selectedJunction?.id,
    selectedJunction?.notes,
    selectedJunction?.purpose_label,
    selectedJunction?.scale,
    selectedJunction?.label_font_size,
  ]);

  // Get connected paths
  const connectedPaths = useMemo(() => {
    if (!selectedJunction || !cablePaths) return [];
    return cablePaths.filter(
      p => p.start_junction_id === selectedJunction.id || p.end_junction_id === selectedJunction.id,
    );
  }, [selectedJunction, cablePaths]);

  // Get product info if junction has a product_id
  const { data: linkedProduct } = useProduct(selectedJunction?.product_id || undefined);

  // Check if junction can be deleted (only if no connected paths)
  const canDelete = useMemo(() => {
    if (!selectedJunction) return false;
    return connectedPaths.length === 0;
  }, [selectedJunction, connectedPaths]);

  // Handle close - save unsaved notes and purpose label before closing
  const handleClose = useCallback(async () => {
    if (selectedJunction) {
      const updates: Record<string, any> = {};

      // Save notes if they've changed
      if (tempNotes !== (selectedJunction.notes || '')) {
        updates.notes = tempNotes;
      }

      // Save purpose label if it's changed
      if (tempPurposeLabel !== (selectedJunction.purpose_label || '')) {
        updates.purpose_label = tempPurposeLabel;
      }

      // Only make API call if there are changes
      if (Object.keys(updates).length > 0) {
        try {
          await updateJunction(selectedJunction.id, updates);
        } catch (error) {
          logger.error('Failed to save on close:', error);
        }
      }
    }
    setActiveDrawer(null);
    clearEntitySelection('cableJunction');
    setEditingName(false);
  }, [
    setActiveDrawer,
    clearEntitySelection,
    selectedJunction,
    tempNotes,
    tempPurposeLabel,
    updateJunction,
  ]);

  // Handle name edit start
  const handleStartEditName = () => {
    if (!selectedJunction) return;
    setTempName(selectedJunction.name);
    setEditingName(true);
  };

  // Handle name save
  const handleSaveName = async () => {
    if (!selectedJunction || !tempName.trim()) return;
    setIsUpdating(true);
    try {
      await updateJunction(selectedJunction.id, { name: tempName.trim() });
      toast.success('Junction name updated');
      setEditingName(false);
    } catch (error) {
      logger.error('Failed to update junction name:', error);
      toast.error('Failed to update junction name');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle notes change
  const handleNotesChange = async (notes: string) => {
    if (!selectedJunction) return;
    setIsUpdating(true);
    try {
      await updateJunction(selectedJunction.id, { notes });
    } catch (error) {
      logger.error('Failed to update notes:', error);
      toast.error('Failed to update notes');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle product selection
  const handleProductSelect = async (productId: string | null) => {
    if (!selectedJunction) return;
    setIsUpdating(true);
    try {
      await updateJunction(selectedJunction.id, { product_id: productId });
      toast.success(productId ? 'Product assigned' : 'Product removed');
      setShowProductModal(false);
    } catch (error) {
      logger.error('Failed to update product:', error);
      toast.error('Failed to update product');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle delete junction
  const handleDelete = async () => {
    if (!selectedJunction) return;
    setIsDeleting(true);
    try {
      await deleteJunction(selectedJunction.id);
      toast.success('Junction deleted');
      handleClose();
    } catch (error) {
      logger.error('Failed to delete junction:', error);
      toast.error('Failed to delete junction');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle start new path from junction
  const handleStartNewPath = () => {
    if (!selectedJunction) return;
    // Close drawer
    handleClose();
    // Start cable path drawing mode from this junction
    const startCablePathDrawing = useAppStore.getState().startCablePathDrawing;
    setActiveMode('cable-path-drawing');
    startCablePathDrawing(selectedJunction.id);
    toast.info('Click to draw a new path from this junction');
  };

  // Handle path click
  const handlePathClick = (pathId: string) => {
    clearEntitySelection('cableJunction');
    selectEntity('cablePath', pathId);
    setActiveDrawer('cablePath');
  };

  // Handle scale slider change (live preview)
  const handleScaleChange = (value: number) => {
    setTempScale(value);
    setHasUnsavedScale(true);

    // Update visual scale via DOM for live preview
    // Find the junction SVG element and update its scale
    if (selectedJunction) {
      const junctionGroup = document.querySelector(`[data-junction-id="${selectedJunction.id}"]`);
      if (junctionGroup) {
        // The junction renderer uses effectiveScale for sizing
        // We'll add a CSS transform for live preview
        const iconGroup = junctionGroup.querySelector('g[transform]');
        if (iconGroup) {
          // Recalculate transform based on new scale
          const baseSize = 24; // ICON_BASE_SIZE from CableJunctionRenderer
          const newIconSize = baseSize * value;
          const halfSize = newIconSize / 2;
          iconGroup.setAttribute(
            'transform',
            `translate(${selectedJunction.x - halfSize}, ${selectedJunction.y - halfSize}) scale(${newIconSize / 24})`,
          );
        }
      }
    }
  };

  // Handle scale save
  const handleScaleSave = async () => {
    if (!selectedJunction) return;
    setIsUpdating(true);
    try {
      await updateJunction(selectedJunction.id, { scale: tempScale });
      toast.success('Scale updated');
      setHasUnsavedScale(false);
    } catch (error) {
      logger.error('Failed to update scale:', error);
      toast.error('Failed to update scale');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle scale cancel
  const handleScaleCancel = () => {
    const originalScale = selectedJunction?.scale || 1.0;
    setTempScale(originalScale);
    setHasUnsavedScale(false);

    // Revert visual preview
    if (selectedJunction) {
      const junctionGroup = document.querySelector(`[data-junction-id="${selectedJunction.id}"]`);
      if (junctionGroup) {
        const iconGroup = junctionGroup.querySelector('g[transform]');
        if (iconGroup) {
          const baseSize = 24;
          const newIconSize = baseSize * originalScale;
          const halfSize = newIconSize / 2;
          iconGroup.setAttribute(
            'transform',
            `translate(${selectedJunction.x - halfSize}, ${selectedJunction.y - halfSize}) scale(${newIconSize / 24})`,
          );
        }
      }
    }
  };

  // Handle label font size change (live preview)
  const handleLabelFontSizeChange = (value: number) => {
    setTempLabelFontSize(value);
    setHasUnsavedLabelFontSize(true);

    // Update visual via DOM for live preview
    if (selectedJunction) {
      const labelGroup = document.querySelector(`[data-purpose-label="${selectedJunction.id}"]`);
      if (labelGroup) {
        const textElement = labelGroup.querySelector('text');
        if (textElement) {
          textElement.setAttribute('font-size', String(value));
          // Update tspan elements too
          const tspans = textElement.querySelectorAll('tspan');
          tspans.forEach(tspan => {
            // Don't override font-size on tspans, they inherit from text
          });
        }
      }
    }
  };

  // Handle label font size cancel
  const handleLabelFontSizeCancel = () => {
    const originalSize = selectedJunction?.label_font_size ?? 10;
    setTempLabelFontSize(originalSize);
    setHasUnsavedLabelFontSize(false);

    // Revert visual preview
    if (selectedJunction) {
      const labelGroup = document.querySelector(`[data-purpose-label="${selectedJunction.id}"]`);
      if (labelGroup) {
        const textElement = labelGroup.querySelector('text');
        if (textElement) {
          textElement.setAttribute('font-size', String(originalSize));
        }
      }
    }
  };

  // Handle apply scale to all junctions
  const handleApplyToAll = async () => {
    if (!junctions || junctions.length === 0) return;
    setIsUpdating(true);
    try {
      // Update all junctions with the current scale
      const updatePromises = junctions.map(junction =>
        updateJunction(junction.id, { scale: tempScale }),
      );
      await Promise.all(updatePromises);
      setHasUnsavedScale(false);
      toast.success(`Updated ${junctions.length} junction${junctions.length !== 1 ? 's' : ''}`);
    } catch (error) {
      logger.error('Failed to apply scale to all junctions:', error);
      toast.error('Failed to apply scale to all junctions');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle set as default for new junctions
  const handleSetAsDefault = async () => {
    if (!saveFloorPlanMetadata) return;
    setIsUpdating(true);
    try {
      const currentMarkerSizes = (floorPlanMetadata?.marker_sizes as MarkerSizesConfig) || {};
      const newMarkerSizes: MarkerSizesConfig = {
        ...currentMarkerSizes,
        junction_default_scale: tempScale,
      };
      const success = await saveFloorPlanMetadata({ marker_sizes: newMarkerSizes });
      if (success) {
        toast.success(`Default junction scale set to ${Math.round(tempScale * 100)}%`);
      }
    } catch (error) {
      logger.error('Failed to save default junction scale:', error);
      toast.error('Failed to save default junction scale');
    } finally {
      setIsUpdating(false);
    }
  };

  // Don't render if not open or no junction selected
  if (!isOpen || !selectedJunction) return null;

  return (
    <>
      <Sheet open={isOpen} modal={false} onOpenChange={open => !open && handleClose()}>
        <SheetContent
          side="right"
          className="w-[400px] overflow-y-auto [&>button]:hidden"
          onPointerDownOutside={e => {
            // Don't close if product modal is open or clicking cable edit handles
            const target = e.target as HTMLElement | SVGElement;
            if (
              showProductModal ||
              target?.closest?.('[data-handle-type]') ||
              target?.closest?.('[data-path-handles]') ||
              target?.closest?.('[data-edit-overlay]')
            ) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            handleClose();
          }}
          onInteractOutside={e => {
            // Don't close if product modal is open or interacting with cable edit handles
            const target = e.target as HTMLElement | SVGElement;
            if (
              showProductModal ||
              target?.closest?.('[data-handle-type]') ||
              target?.closest?.('[data-path-handles]') ||
              target?.closest?.('[data-edit-overlay]')
            ) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            handleClose();
          }}
        >
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <Box className="h-5 w-5" />
                Junction Box
              </SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-8 w-8"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Junction Name */}
            <div className="space-y-2">
              <Label>Name</Label>
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={tempName}
                    onChange={e => setTempName(e.target.value)}
                    onKeyDown={e => {
                      // Stop propagation to prevent global handlers from intercepting
                      e.stopPropagation();
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                    onKeyUp={e => e.stopPropagation()}
                    autoFocus
                    disabled={isUpdating}
                  />
                  <Button size="sm" onClick={handleSaveName} disabled={isUpdating}>
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingName(false)}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                  <span className="font-medium text-lg">{selectedJunction.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleStartEditName}
                    className="h-8 w-8"
                  >
                    <SquarePen className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <Separator />

            {/* Product Assignment */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Assigned Product
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowProductModal(true)}
                  disabled={isUpdating}
                >
                  {linkedProduct ? 'Change' : 'Assign'}
                </Button>
              </div>

              {linkedProduct ? (
                <div className="p-4 bg-muted rounded-lg space-y-3">
                  {/* Product Image */}
                  {(() => {
                    const imageUrl =
                      linkedProduct?.image_url || (linkedProduct?.portal_metadata as any)?.imageUrl;

                    if (!imageUrl) return null;

                    return (
                      <div className="relative aspect-square w-24 h-24 mx-auto overflow-hidden rounded-lg border bg-muted">
                        <img
                          src={imageUrl}
                          alt={`${linkedProduct.brand} ${linkedProduct.model}`}
                          className="h-full w-full object-contain p-1.5"
                          onError={e => {
                            const container = e.currentTarget.parentElement;
                            if (container) {
                              container.style.display = 'none';
                            }
                          }}
                        />
                      </div>
                    );
                  })()}

                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="font-medium">{linkedProduct.brand}</p>
                      <p className="text-sm text-muted-foreground">{linkedProduct.model}</p>
                      {linkedProduct.part_number && (
                        <p className="text-xs font-mono text-muted-foreground">
                          {linkedProduct.part_number}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleProductSelect(null)}
                      disabled={isUpdating}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {linkedProduct.description && (
                    <p className="text-sm text-muted-foreground">{linkedProduct.description}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No product assigned. Assign an enclosure or junction box from the product library.
                </p>
              )}
            </div>

            <Separator />

            {/* Purpose Label */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Purpose Label
              </Label>

              {/* Purpose Type Toggle */}
              <ToggleGroup
                type="single"
                value={selectedJunction.purpose_type || ''}
                onValueChange={value => {
                  if (value === '') {
                    // Clear purpose
                    setTempPurposeLabel('');
                    updateJunction(selectedJunction.id, {
                      purpose_type: null,
                      purpose_label: null,
                    });
                  } else {
                    updateJunction(selectedJunction.id, {
                      purpose_type: value as JunctionPurposeType,
                    });
                  }
                }}
                className="grid grid-cols-2 gap-2"
              >
                <ToggleGroupItem
                  value="from"
                  aria-label="From (Source)"
                  className="flex-1 flex items-center justify-center gap-1"
                >
                  <ArrowDownRight className="h-4 w-4 text-emerald-600" />
                  From
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="to"
                  aria-label="To (Destination)"
                  className="flex-1 flex items-center justify-center gap-1"
                >
                  <Target className="h-4 w-4 text-amber-600" />
                  To
                </ToggleGroupItem>
              </ToggleGroup>

              {/* Purpose Label Input */}
              {selectedJunction.purpose_type && (
                <Input
                  placeholder={
                    selectedJunction.purpose_type === 'from'
                      ? 'e.g., Main Rack, Panel A'
                      : 'e.g., Outdoor Speakers, Display Zone 1'
                  }
                  value={tempPurposeLabel}
                  onChange={e => setTempPurposeLabel(e.target.value)}
                  onKeyDown={e => e.stopPropagation()}
                  onKeyUp={e => e.stopPropagation()}
                  onBlur={() => {
                    // Save on blur if changed
                    if (tempPurposeLabel !== (selectedJunction.purpose_label || '')) {
                      updateJunction(selectedJunction.id, {
                        purpose_label: tempPurposeLabel,
                      });
                    }
                  }}
                />
              )}

              <p className="text-xs text-muted-foreground">
                Add a label to show where cables are coming from or going to. The label appears as a
                draggable note connected to the junction.
              </p>

              {/* Label Text Size - only show when purpose type is set */}
              {selectedJunction.purpose_type && (
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Label Text Size</Label>
                    <span className="text-sm font-medium">{tempLabelFontSize}px</span>
                  </div>
                  <Slider
                    min={8}
                    max={24}
                    step={1}
                    value={[tempLabelFontSize]}
                    onValueChange={([value]) => handleLabelFontSizeChange(value)}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>8px</span>
                    <span>16px</span>
                    <span>24px</span>
                  </div>
                  {hasUnsavedLabelFontSize && (
                    <div className="flex gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLabelFontSizeCancel}
                        disabled={isUpdating}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={async () => {
                          await updateJunction(selectedJunction.id, {
                            label_font_size: tempLabelFontSize,
                          });
                          setHasUnsavedLabelFontSize(false);
                          toast.success('Label text size updated');
                        }}
                        disabled={isUpdating}
                        className="flex-1"
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Separator />

            {/* Icon Size */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Maximize2 className="h-4 w-4" />
                  Icon Size
                </Label>
                <span className="text-sm font-medium">{Math.round(tempScale * 100)}%</span>
              </div>

              <Slider
                min={0.2}
                max={5.0}
                step={0.05}
                value={[tempScale]}
                onValueChange={([value]) => handleScaleChange(value)}
                className="w-full"
              />

              {/* Scale range labels */}
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>20%</span>
                <span>100%</span>
                <span>500%</span>
              </div>

              {/* Save/Cancel buttons when scale changed */}
              {hasUnsavedScale && (
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleScaleCancel}
                    disabled={isUpdating}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleScaleSave}
                    disabled={isUpdating}
                    className="flex-1"
                  >
                    {isUpdating ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}

              {/* Apply to All and Set as Default buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleApplyToAll}
                  disabled={isUpdating || junctions.length <= 1}
                  className="flex-1 text-xs"
                >
                  Apply to All ({junctions.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSetAsDefault}
                  disabled={isUpdating}
                  className="flex-1 text-xs"
                >
                  Set as Default
                </Button>
              </div>
            </div>

            <Separator />

            {/* Connected Paths */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Cable className="h-4 w-4" />
                  Connected Paths
                </Label>
                <Badge variant="secondary">{connectedPaths.length}</Badge>
              </div>

              {connectedPaths.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No paths connected to this junction
                </p>
              ) : (
                <div className="space-y-2">
                  {connectedPaths.map(path => {
                    const isStart = path.start_junction_id === selectedJunction.id;
                    const otherJunctionId = isStart ? path.end_junction_id : path.start_junction_id;
                    const otherJunction = otherJunctionId
                      ? junctions?.find(j => j.id === otherJunctionId)
                      : null;

                    return (
                      <div
                        key={path.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted"
                        onClick={() => handlePathClick(path.id)}
                      >
                        <div className="flex items-center gap-2">
                          <Cable className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {isStart ? 'To' : 'From'}{' '}
                            <span className="font-medium">{otherJunction?.name || 'Unknown'}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {path.cables.length} cable{path.cables.length !== 1 ? 's' : ''}
                          </Badge>
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <Button variant="outline" className="w-full" onClick={handleStartNewPath}>
                <Plus className="h-4 w-4 mr-2" />
                Draw New Path from Here
              </Button>
            </div>

            <Separator />

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Add notes about this junction..."
                value={tempNotes}
                onChange={e => setTempNotes(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                onKeyUp={e => e.stopPropagation()}
                onBlur={() => {
                  if (selectedJunction && tempNotes !== (selectedJunction.notes || '')) {
                    handleNotesChange(tempNotes);
                  }
                }}
                disabled={isUpdating}
                className="min-h-[80px]"
              />
            </div>

            <Separator />

            {/* Delete Button */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="w-full"
                  disabled={isDeleting || !canDelete}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Junction
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Junction Box?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {canDelete
                      ? 'This will permanently delete this junction box.'
                      : 'This junction cannot be deleted because it has connected cable paths. Delete the connected paths first.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  {canDelete && (
                    <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </AlertDialogAction>
                  )}
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {!canDelete && connectedPaths.length > 0 && (
              <p className="text-xs text-muted-foreground text-center">
                Delete connected paths first to remove this junction.
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Product Selection Modal */}
      <ProductSelectionModal
        open={showProductModal}
        onOpenChange={setShowProductModal}
        onSelect={product => handleProductSelect(product?.id || null)}
        title="Select Product for Junction Box"
        showAllByDefault
      />
    </>
  );
}
